// color design tokens export
export const tokensDark = {
  grey: {
    0: "#ffffff", // manually adjusted
    10: "#f6f6f6", // manually adjusted
    50: "#f0f0f0", // manually adjusted
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
    1000: "#000000", // manually adjusted
  },
  primary: {
    // phieg primary — evolved sage on dark teal. Values preserved to keep
    // ~30 downstream files working without per-file churn; only primary[400]
    // (the main brand accent) is bumped one step more saturated.
    50: "#cce7a1",
    100: "#eff5f4",
    200: "#d0e2de",
    300: "#b0cec9",
    400: "#7fb09a", // was #91bbb3 — evolved sage, same family, more confident
    500: "#FFFFFF",
    600: "#1a2a27",
    700: "#446e66",
    800: "#314f49",
    900: "#1d2f2c",
  },
  secondary: {
    // phieg secondary — used as foreground text token, kept as is
    50: "#0d0d0d",
    100: "#0d0d0d",
    200: "#0d0d0d",
    300: "#0d0d0d",
    400: "#0d0d0d",
    500: "#0d0d0d",
    600: "#0d0d0d",
    700: "#0d0d0d",
    800: "#0d0d0d",
    900: "#0d0d0d",
  },
};

// Hoisted so both `extras.surface`/`extras.feedback` AND `extras.mb.dark`
// reference ONE definition — no mirrored literals to drift.
const surface = {
  base: "#1a2a27",
  raised: "#213432", // subtle lift above base
  hover: "#273e3b",
  borderSubtle: "rgba(255, 255, 255, 0.08)",
  borderDefault: "rgba(255, 255, 255, 0.12)",
  textHigh: "#eaf2ef",
  textMid: "#a8bcb6",
  textLow: "#6f8782",
};

const feedback = {
  success: "#22c55e",
  successSoft: "rgba(34, 197, 94, 0.16)",
  warning: "#f59e0b",
  warningSoft: "rgba(245, 158, 11, 0.18)",
  danger: "#ef4444",
  dangerSoft: "rgba(239, 68, 68, 0.18)",
  info: "#60a5fa",
  infoSoft: "rgba(96, 165, 250, 0.18)",
  neutral: "#94a3b8",
  neutralSoft: "rgba(148, 163, 184, 0.18)",
};

// Semantic token extensions added for the visual uplift. These sit alongside
// the existing palette so we can opt into them without breaking 30+ files
// that reference the original palette keys. Consumed via
// `theme.palette.extras.*` inside components.
export const extras = {
  // Accent family — evolved sage used for primary CTAs, focus rings, active nav
  accent: {
    main: "#7fb09a", // aligned with primary[400]
    hover: "#6a9c86",
    soft: "#a6c9ba",
    subtle: "rgba(127, 176, 154, 0.14)",
    contrastText: "#0b1412",
  },
  // Dark surfaces — used by components that want to sit above the dark teal base
  surface,
  // Semantic feedback colors — used by MuiChip variants and status pills.
  // Independent from brand accent so red/orange/blue don't compete.
  feedback,
  // Elevation / shadow scale — tuned for dark backgrounds
  shadow: {
    xs: "0 1px 2px rgba(0,0,0,0.18)",
    sm: "0 2px 6px rgba(0,0,0,0.22)",
    md: "0 8px 20px rgba(0,0,0,0.26)",
    lg: "0 16px 36px rgba(0,0,0,0.32)",
    focusRing: "0 0 0 3px rgba(127, 176, 154, 0.35)",
  },
  radius: {
    sm: 8,
    md: 10,
    lg: 14,
    xl: 18,
    pill: 9999,
  },
  // Frosted-glass surfaces (mobile chrome). Always pair with a solid
  // surface.* fallback — backdrop-filter support is gated via @supports.
  glass: {
    bg: "rgba(26, 42, 39, 0.78)",
    raised: "rgba(33, 52, 50, 0.78)",
    blur: "saturate(1.5) blur(16px)",
    edge: "rgba(255, 255, 255, 0.10)",
  },
  // Shared motion curves — spring overshoots slightly, ease matches the
  // global 160ms component transitions.
  motion: {
    spring: "cubic-bezier(0.2, 0.9, 0.3, 1.2)",
    ease: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  // Radial sage glow behind the mobile Today header.
  heroGlow:
    "radial-gradient(120% 70% at 50% 0%, rgba(127, 176, 154, 0.16), transparent 60%)",
  // Signature Phieg gradient — interface sage into the brighter logo green.
  // Reserved for clipped-text hero moments (Today greeting, FM hero numbers);
  // fills, pills and progress bars stay flat accent so the logo green keeps
  // its scarcity.
  brandGradient: "linear-gradient(135deg, #7fb09a 0%, #A9D98B 100%)",
  // Faint top-light sheen layered over dark raised cards via backgroundImage.
  cardSheen:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent 45%)",
  // Media Buying — ONE dark token set. The module used to render white cards
  // ("island") on the dark chrome with a mobile-only dark twin under mb.dark;
  // the 2026-07 dark unification promoted that twin to be THE set (desktop +
  // mobile), deleting the dual-surface system that kept producing invisible
  // dark-ink-on-dark bugs. Every value derives from the hoisted surface/
  // feedback consts — the same language the rest of the app's dark chrome and
  // lead chips use. Status vocabulary mirrors lib/mediaBuying/tiers.js.
  mb: {
    cardBg: surface.raised,
    cardBorder: surface.borderSubtle,
    ink: surface.textHigh,
    inkMid: surface.textMid,
    inkLow: surface.textLow,
    tileBg: "rgba(255, 255, 255, 0.05)",
    // SOLID — sticky table headers can't be translucent over scrolling rows.
    tableHeadBg: "#243836",
    tableHeadText: surface.textMid,
    divider: "rgba(255, 255, 255, 0.06)",
    border: surface.borderSubtle,
    scrollThumb: "rgba(255, 255, 255, 0.18)",
    accent: "#7fb09a", // = extras.accent.main
    accentChipBg: "rgba(127, 176, 154, 0.14)", // = accent.subtle
    accentChipText: "#a6c9ba", // = accent.soft
    hoverRow: "rgba(255, 255, 255, 0.05)",
    // ONE status-chip size for BULK / VIP / HOLD / health (was 18/19/20/22px).
    chip: { height: 20, fontSize: 10 },
    // Named padding scale — kills the "1.1rem 1.25rem" vs "0.9rem 1.05rem" drift.
    spacing: {
      cardPad: "1.1rem 1.25rem",
      tilePad: "0.6rem 0.8rem",
      rowPad: "0.55rem 0.8rem",
      miniPad: "0.5rem 0.7rem",
    },
    // HOLD = a managed/paused state. Sage-tinted (NOT the health green) so it
    // never reads as a "healthy" signal.
    hold: { fg: "#a6c9ba", bg: "rgba(127, 176, 154, 0.14)" },
    // Foregrounds are the feedback 400-series with *Soft translucent fills —
    // the app's dark chip language (strong light-surface hexes are low-contrast
    // as text on dark).
    status: {
      good: feedback.success,
      goodBg: feedback.successSoft,
      warn: feedback.warning,
      warnBg: feedback.warningSoft,
      bad: feedback.danger,
      badBg: feedback.dangerSoft,
      info: feedback.neutral,
      infoBg: feedback.neutralSoft,
      vip: "#e2b366",
      vipBg: "rgba(212, 149, 56, 0.18)",
      bulk: "#a78bfa",
      bulkBg: "rgba(124, 58, 237, 0.22)",
      // Server-vocabulary aliases (mirror lib/mediaBuying/tiers.js) so panels can
      // pass server-computed green/yellow/red/na straight through. Same hexes.
      green: feedback.success,
      greenBg: feedback.successSoft,
      yellow: feedback.warning,
      yellowBg: feedback.warningSoft,
      red: feedback.danger,
      redBg: feedback.dangerSoft,
      na: feedback.neutral,
      naBg: feedback.neutralSoft,
    },
  },
};

// function that reverses the color palette
function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

const FONT_STACK = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

// mui theme settings
export const themeSettings = (mode) => {
  const isDark = mode === "dark";

  return {
    palette: {
      mode: mode,
      ...(isDark
        ? {
            // palette values for dark mode
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[400],
              light: tokensDark.primary[400],
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[300],
              light: tokensDark.secondary[400],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.primary[600],
              alt: tokensDark.primary[500],
              // Raw <Paper> (menus, dialogs, popovers, the FM mobile cards)
              // used to fall back to MUI's near-black #121212 — the one
              // surface that wasn't in the brand palette. Same lift as the
              // raised card, so every popup sits in the dark-teal family.
              paper: extras.surface.raised,
            },
            extras,
          }
        : {
            // palette values for light mode
            primary: {
              ...tokensLight.primary,
              main: tokensDark.grey[50],
              light: tokensDark.grey[100],
            },
            secondary: {
              ...tokensLight.secondary,
              main: tokensDark.secondary[600],
              light: tokensDark.secondary[700],
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.grey[0],
              alt: tokensDark.grey[50],
            },
            extras,
          }),
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: FONT_STACK,
      fontSize: 12,
      // Display headings lean on the true Inter 800 (the wordmark's weight)
      // with tighter tracking — the brand's typographic voice, no second
      // font family needed.
      h1: {
        fontFamily: FONT_STACK,
        fontSize: 40,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: FONT_STACK,
        fontSize: 32,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: FONT_STACK,
        fontSize: 24,
        fontWeight: 700,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontFamily: FONT_STACK,
        fontSize: 20,
        fontWeight: 600,
      },
      h5: {
        fontFamily: FONT_STACK,
        fontSize: 16,
        fontWeight: 500,
      },
      h6: {
        fontFamily: FONT_STACK,
        fontSize: 14,
        fontWeight: 500,
      },
      body1: {
        fontFamily: FONT_STACK,
      },
      body2: {
        fontFamily: FONT_STACK,
      },
      button: {
        fontFamily: FONT_STACK,
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
      overline: {
        fontFamily: FONT_STACK,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: FONT_STACK,
            fontFeatureSettings: '"cv11", "ss01"',
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "*::-webkit-scrollbar": {
            width: 10,
            height: 10,
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(127, 176, 154, 0.35)",
            borderRadius: 10,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(127, 176, 154, 0.55)",
          },
        },
      },
      // "info" alerts in the brand sage instead of MUI's stock blue — the one
      // off-palette color that kept showing up on dark surfaces (push nudge,
      // helper notes). Warning / error / success keep their semantic colors.
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: extras.radius.md,
            alignItems: "center",
          },
          standardInfo: {
            backgroundColor: "rgba(127, 176, 154, 0.12)",
            color: extras.surface.textHigh,
            "& .MuiAlert-icon": { color: extras.accent.main },
            "& .MuiAlert-action": { color: extras.surface.textMid },
          },
          outlinedInfo: {
            backgroundColor: "rgba(127, 176, 154, 0.08)",
            borderColor: "rgba(127, 176, 154, 0.35)",
            color: extras.surface.textHigh,
            "& .MuiAlert-icon": { color: extras.accent.main },
            "& .MuiAlert-action": { color: extras.surface.textMid },
          },
          filledInfo: {
            backgroundColor: extras.accent.main,
            color: extras.accent.contrastText,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            minHeight: 40,
            paddingLeft: 18,
            paddingRight: 18,
            fontWeight: 600,
            letterSpacing: "0.02em",
            boxShadow: "none",
            transition:
              "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
            "&:hover": {
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              boxShadow: extras.shadow.focusRing,
            },
          },
          contained: {
            backgroundColor: extras.accent.main,
            color: extras.accent.contrastText,
            "&:hover": {
              backgroundColor: extras.accent.hover,
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(127, 176, 154, 0.35)",
              color: "rgba(11, 20, 18, 0.6)",
            },
          },
          outlined: {
            borderColor: extras.accent.main,
            color: extras.accent.soft,
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: extras.accent.hover,
              backgroundColor: extras.accent.subtle,
            },
          },
          text: {
            color: extras.accent.soft,
            "&:hover": {
              backgroundColor: extras.accent.subtle,
            },
          },
          sizeSmall: {
            minHeight: 32,
            paddingLeft: 12,
            paddingRight: 12,
          },
          sizeLarge: {
            minHeight: 46,
            paddingLeft: 22,
            paddingRight: 22,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: "background-color 160ms ease",
            "&.Mui-focusVisible": {
              boxShadow: extras.shadow.focusRing,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          rounded: {
            borderRadius: 14,
          },
          elevation1: {
            boxShadow: extras.shadow.sm,
          },
          elevation2: {
            boxShadow: extras.shadow.sm,
          },
          elevation3: {
            boxShadow: extras.shadow.md,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${extras.surface.borderSubtle}`,
            boxShadow: extras.shadow.sm,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 40,
            transition: "border-color 160ms ease, box-shadow 160ms ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: extras.surface.borderDefault,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: extras.accent.soft,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: extras.accent.main,
              borderWidth: 1,
            },
            "&.Mui-focused": {
              boxShadow: extras.shadow.focusRing,
            },
          },
          input: {
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            overflow: "hidden",
            "&:before, &:after": { display: "none" },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            letterSpacing: "0.01em",
            "&.Mui-focused": {
              color: extras.accent.main,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {},
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: extras.shadow.md,
            border: `1px solid ${extras.surface.borderSubtle}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginLeft: 4,
            marginRight: 4,
            minHeight: 36,
            "&.Mui-selected": {
              backgroundColor: extras.accent.subtle,
              "&:hover": {
                backgroundColor: extras.accent.subtle,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 9999,
            fontWeight: 600,
            letterSpacing: "0.02em",
            fontSize: 11,
            height: 24,
            paddingLeft: 4,
            paddingRight: 4,
            textTransform: "uppercase",
          },
          // `color="info|warning|success|error"` chips take the app's soft
          // chip language (translucent fill, semantic ink) instead of MUI's
          // saturated solid fills — same recipe as feedbackChipProps.
          colorInfo: {
            backgroundColor: extras.feedback.infoSoft,
            color: extras.feedback.info,
            border: `1px solid ${extras.feedback.info}`,
          },
          colorWarning: {
            backgroundColor: extras.feedback.warningSoft,
            color: extras.feedback.warning,
            border: `1px solid ${extras.feedback.warning}`,
          },
          colorSuccess: {
            backgroundColor: extras.feedback.successSoft,
            color: extras.feedback.success,
            border: `1px solid ${extras.feedback.success}`,
          },
          colorError: {
            backgroundColor: extras.feedback.dangerSoft,
            color: extras.feedback.danger,
            border: `1px solid ${extras.feedback.danger}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: extras.surface.borderSubtle,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: extras.shadow.lg,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: 18,
            fontWeight: 600,
            paddingTop: 20,
            paddingBottom: 12,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            borderRight: `1px solid ${extras.surface.borderSubtle}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow: "none",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginLeft: 8,
            marginRight: 8,
            paddingTop: 10,
            paddingBottom: 10,
            transition:
              "background-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
            "&:hover": {
              backgroundColor: "rgba(127, 176, 154, 0.10)",
            },
            "&.Mui-focusVisible": {
              boxShadow: extras.shadow.focusRing,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 36,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#0f1b19",
            border: `1px solid ${extras.surface.borderDefault}`,
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 500,
            paddingTop: 6,
            paddingBottom: 6,
          },
          arrow: {
            color: "#0f1b19",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            height: 6,
            backgroundColor: "rgba(255,255,255,0.08)",
          },
          bar: {
            borderRadius: 999,
            backgroundColor: extras.accent.main,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: extras.shadow.md,
            border: `1px solid ${extras.surface.borderSubtle}`,
          },
          listbox: {
            padding: 4,
          },
          option: {
            borderRadius: 8,
            margin: 2,
            minHeight: 36,
            '&[aria-selected="true"]': {
              backgroundColor: extras.accent.subtle,
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "none",
            borderRadius: 14,
            fontVariantNumeric: "tabular-nums",
            backgroundColor: extras.surface.raised,
            color: extras.surface.textHigh,
            "--DataGrid-rowBorderColor": extras.surface.borderSubtle,
          },
          columnHeaders: {
            backgroundColor: extras.surface.hover,
            color: extras.surface.textMid,
            borderBottom: `1px solid ${extras.surface.borderDefault}`,
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
            minHeight: "48px !important",
            maxHeight: "48px !important",
            lineHeight: "48px !important",
          },
          columnHeader: {
            borderRight: `1px solid ${extras.surface.borderDefault}`,
            "&:last-child": {
              borderRight: "none",
            },
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          columnHeaderTitle: {
            fontWeight: 600,
            color: extras.surface.textMid,
          },
          columnSeparator: {
            display: "none",
          },
          cell: {
            color: extras.surface.textHigh,
            borderBottom: `1px solid ${extras.surface.borderSubtle}`,
            borderRight: `1px solid ${extras.surface.borderSubtle}`,
            "&:last-child": {
              borderRight: "none",
            },
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          row: {
            "&:hover": {
              backgroundColor: "rgba(127, 176, 154, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor: extras.accent.subtle,
              "&:hover": {
                backgroundColor: extras.accent.subtle,
              },
            },
          },
          footerContainer: {
            backgroundColor: extras.surface.raised,
            color: extras.surface.textMid,
            borderTop: `1px solid ${extras.surface.borderDefault}`,
          },
          toolbarContainer: {
            backgroundColor: extras.surface.raised,
            padding: "12px 12px 8px 12px",
            gap: 8,
          },
          virtualScroller: {
            backgroundColor: extras.surface.raised,
          },
          overlay: {
            backgroundColor: extras.surface.raised,
            color: extras.surface.textMid,
          },
          iconSeparator: {
            color: extras.surface.borderDefault,
          },
          menuIcon: {
            color: extras.surface.textMid,
          },
          sortIcon: {
            color: extras.surface.textMid,
          },
          checkboxInput: {
            color: extras.surface.textMid,
            "&.Mui-checked": {
              color: extras.accent.main,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            color: extras.surface.textMid,
          },
          selectIcon: {
            color: extras.surface.textMid,
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: "wave",
        },
        styleOverrides: {
          root: {
            // Phone layouts render skeletons on dark raised surfaces where the
            // default light-scheme grey is nearly invisible. Desktop skeletons
            // sit on white cards and keep the MUI defaults.
            "@media (max-width:600px)": {
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              "&::after": {
                background:
                  "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent)",
              },
            },
          },
        },
      },
    },
  };
};
